import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../api/api";
import toast from "react-hot-toast";
import {
  Eye,
  Download,
  Search,
  ShieldCheck,
  CheckCircle,
  Clock,
  Inbox,
} from "lucide-react";

const statusColors = {
  received: "bg-blue-50 text-blue-700",
  in_review: "bg-yellow-50 text-yellow-700",
  resolved: "bg-green-50 text-green-700",
  closed: "bg-slate-100 text-slate-700",
};

const AdminComplaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    status: "",
    complaint_type: "",
    search: "",
  });

  const loadComplaints = async () => {
    try {
      setLoading(true);

      const query = new URLSearchParams();

      if (filters.status) query.append("status", filters.status);
      if (filters.complaint_type) query.append("complaint_type", filters.complaint_type);
      if (filters.search) query.append("search", filters.search);

      const res = await api.get(`/admin/complaints?${query.toString()}`);
      setComplaints(res.data || []);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load complaints");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get("/complaints/public-summary");
      setSummary(res.data);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load summary");
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    loadComplaints();
  }, [filters.status, filters.complaint_type]);

  const searchNow = (e) => {
    e.preventDefault();
    loadComplaints();
  };

  const exportComplaints = async () => {
    try {
      const res = await api.get("/admin/complaints-export", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "complaints-data-bank.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Complaints exported");
    } catch (err) {
      toast.error(err.cleanMessage || "Export failed");
    }
  };

  const cards = [
    { label: "Total Received", value: summary?.total_received || 0, icon: Inbox },
    { label: "In Review", value: summary?.in_review || 0, icon: Clock },
    { label: "Resolved", value: summary?.resolved || 0, icon: CheckCircle },
    { label: "New", value: summary?.received || 0, icon: ShieldCheck },
  ];

  return (
    <div className="w-full max-w-full overflow-hidden">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[#0b6b1d]">
            Complaints Data Bank
          </h1>

          <p className="text-slate-500 mt-1 max-w-3xl">
            Manage citizen complaints, respond privately, export reports and track resolution.
          </p>
        </div>

        <button
          onClick={exportComplaints}
          className="w-fit px-5 py-3 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black flex items-center gap-2"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-white rounded-3xl p-6 shadow-sm border min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-[#0b6b1d]/10 text-[#0b6b1d] flex items-center justify-center mb-4">
              <Icon size={22} />
            </div>

            <p className="text-slate-500 text-sm">{label}</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">
              {value}
            </h3>
          </div>
        ))}
      </div>

      <form
        onSubmit={searchNow}
        className="bg-white rounded-3xl border border-slate-200 p-5 shadow-sm mb-6 grid grid-cols-1 lg:grid-cols-[1fr_180px_230px_auto] gap-4"
      >
        <div className="relative min-w-0">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            value={filters.search}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
            placeholder="Search tracking number, name, school, LGA..."
            className="w-full min-w-0 border border-slate-200 rounded-2xl pl-11 pr-4 py-3 outline-none focus:border-[#0b6b1d]"
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, status: e.target.value }))
          }
          className="w-full min-w-0 border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
        >
          <option value="">All Status</option>
          <option value="received">Received</option>
          <option value="in_review">In Review</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>

        <select
          value={filters.complaint_type}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, complaint_type: e.target.value }))
          }
          className="w-full min-w-0 border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
        >
          <option value="">All Types</option>
          <option>Project complaint</option>
          <option>Teacher conduct complaint</option>
          <option>Infrastructure complaint</option>
          <option>Illegal levy complaint</option>
          <option>Pupil welfare complaint</option>
          <option>General feedback</option>
        </select>

        <button className="px-6 py-3 rounded-2xl bg-slate-900 text-white font-black">
          Search
        </button>
      </form>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-6">Loading complaints...</p>
        ) : complaints.length === 0 ? (
          <p className="p-6 text-slate-500">No complaint found.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {complaints.map((item) => {
              const complaintKey = item.id || item.tracking_number;

              return (
                <div
                  key={complaintKey}
                  className="p-5 grid grid-cols-1 xl:grid-cols-[1.1fr_1fr_1fr_160px_60px] gap-4 items-center"
                >
                  <div className="min-w-0">
                    <p className="text-xs text-slate-400 font-black uppercase">
                      Tracking No.
                    </p>
                    <p className="font-black text-[#0b6b1d] break-words">
                      {item.tracking_number}
                    </p>
                  </div>

                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">
                      {item.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {item.phone || item.email || "No contact"}
                    </p>
                  </div>

                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 truncate">
                      {item.school_affected}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {item.lga} • {item.complaint_type}
                    </p>
                  </div>

                  <div>
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-xs font-black uppercase ${
                        statusColors[item.status] || "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {item.status?.replace("_", " ")}
                    </span>
                  </div>

                  <div className="flex xl:justify-end">
                    <Link
                      to={`/admin/complaints/${encodeURIComponent(complaintKey)}`}
                      className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-[#0b6b1d] hover:text-white flex items-center justify-center"
                    >
                      <Eye size={17} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminComplaints;